import { InsideBoundingBox } from 'algoliasearch';
import { CustomStoreHours } from '../interfaces';

export type BoundsData = {
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
};

export enum ESearchRadiusLevels {
  LEVEL_1 = 15000, // zoom level ~12
  LEVEL_2 = 30000, // zoom level ~11
}

export enum ESearchZoomLevels {
  /**
   * Zoom level thresholds corresponding to radius levels
   * - LEVEL_1: ~15km radius
   */
  LEVEL_1 = 12,
  /**
   * Zoom level thresholds corresponding to radius levels
   * - LEVEL_2: ~30km radius
   */
  LEVEL_2 = 11,
}

export const AustraliaBounds = {
  north: -10,
  south: -40,
  east: 160,
  west: 100,
};

// normalize "09.00 pm" -> "09:00 PM" for StoreCard's moment parsing
export const normalizeTimeStr = (t?: string) =>
  t ? t.replace('.', ':').replace(/\s*(am|pm)\s*$/i, (_, ap) => ` ${ap.toUpperCase()}`) : t;

export const normalizeStoreHours = (hours?: CustomStoreHours) => {
  if (!hours) return hours;
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const out = { ...hours };
  for (const day of days) {
    const value = hours[day];
    out[day] = value
      ? {
          ...value,
          openTime: normalizeTimeStr(value.openTime),
          closeTime: normalizeTimeStr(value.closeTime),
        }
      : value;
  }
  return out;
};

export type LatLng = { lat: number; lng: number };
export const haversineKm = (a: LatLng, b: LatLng): number => {
  const R = 6371e3;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dPhi = toRad(b.lat - a.lat);
  const dLambda = toRad(b.lng - a.lng);
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  const s = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return (2 * R * Math.asin(Math.sqrt(s))) / 1000;
};

export const calculateBoundsForRadius = (center: LatLng, radiusInMeters: number): google.maps.LatLngBounds => {
  const earthRadius = 6378137; // Radius of the Earth in meters

  const lat = center.lat;
  const lng = center.lng;

  // Calculate the offsets in degrees
  const latOffset = (radiusInMeters / earthRadius) * (180 / Math.PI);
  const lngOffset = ((radiusInMeters / earthRadius) * (180 / Math.PI)) / Math.cos((lat * Math.PI) / 180);

  const northEast = new google.maps.LatLng(lat + latOffset, lng + lngOffset);
  const southWest = new google.maps.LatLng(lat - latOffset, lng - lngOffset);

  const bounds = new google.maps.LatLngBounds(southWest, northEast);
  return bounds;
};

/**
 * Helper function to convert pixels to degrees based on zoom level
 */
const degreesPerPixel = (zoomLevel: number): number => {
  // 360° divided by the number of pixels at the zoom-level
  return 360 / (Math.pow(2, zoomLevel) * 256);
};

/**
 * insideBoundingBox expects nested array format: [[lat1, lng1, lat2, lng2]]
 * where lat1,lng1 is northeast corner and lat2,lng2 is southwest corner
 */
export const calculateInsideBoundingBox = (
  bounds: google.maps.LatLngBounds | undefined,
  padding?: number,
  zoomLevel?: number,
): InsideBoundingBox | null | undefined => {
  if (!bounds) return undefined;
  // Consider a rectangle that includes the 180th meridian: [70, 170, 60, -170].
  // To resolve the issue, split this rectangle into two smaller ones:
  // Rectangle 1: [70, 170, 60, 180] (east of the meridian)
  // Rectangle 2: [70, -180, 60, -170] (west of the meridian)

  if (bounds) {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Convert pixel padding to degrees if zoom level is provided
    const paddingDegrees = padding && zoomLevel ? degreesPerPixel(zoomLevel) * padding : padding || 0;

    // Apply padding to make bounds smaller (inward padding to prevent marker cutoff)
    const paddedNeLat = Math.min(90, ne.lat() - paddingDegrees);
    const paddedSwLat = Math.max(-90, sw.lat() + paddingDegrees);
    const paddedNeLng = ne.lng() - paddingDegrees;
    const paddedSwLng = sw.lng() + paddingDegrees;

    // Normalize longitude values to stay within -180 to 180 range
    const normalizelng = (lng: number) => {
      while (lng > 180) lng -= 360;
      while (lng < -180) lng += 360;
      return lng;
    };

    const normalizedNeLng = normalizelng(paddedNeLng);
    const normalizedSwLng = normalizelng(paddedSwLng);

    if (normalizedNeLng < normalizedSwLng) {
      // Rectangle crosses the 180th meridian
      return [
        [paddedNeLat, normalizedNeLng, paddedSwLat, 180],
        [paddedNeLat, -180, paddedSwLat, normalizedSwLng],
      ];
    } else {
      // Normal rectangle
      return [[paddedNeLat, normalizedNeLng, paddedSwLat, normalizedSwLng]];
    }
  }
  return null;
};

export const reverseBoxToBoundsData = (box?: InsideBoundingBox | null): google.maps.LatLngBounds[] => {
  const boundsArray: google.maps.LatLngBounds[] = [];
  if (typeof box === 'object' && Array.isArray(box)) {
    box.forEach((boxItem) => {
      if (boxItem.length === 4) {
        const ne = new google.maps.LatLng(boxItem[0], boxItem[1]);
        const sw = new google.maps.LatLng(boxItem[2], boxItem[3]);
        const bounds = new google.maps.LatLngBounds(sw, ne);
        boundsArray.push(bounds);
      }
    });
  }
  return boundsArray;
};
